import { AxiosError } from "axios";

/**
 * Recognises the anti-bot (and send-limit) rejections coming out of `POST /api/auth/send-otp`
 * and turns them into something worth showing a person.
 *
 * Matching is on `code`, never on the message text — the server's copy is a fallback for
 * surfaces that only have room for one line (toasts), and rewording it must not silently
 * stop this from firing.
 *
 * Los tres códigos de siempre (PENDING / EXPIRED / FAILED) siguen vigentes: la API los manda
 * cuando no hay nivel con clic configurado, y los bundles viejos cacheados los siguen recibiendo.
 */

const FALLBACK_RETRY_SECONDS = 5;
/** Un 429 de límite sin `retryAfterSeconds` (no debería pasar): un minuto es lo más honesto. */
const FALLBACK_LIMIT_SECONDS = 60;

export type AuthGuardErrorCode =
  | "TURNSTILE_PENDING"
  | "TURNSTILE_EXPIRED"
  | "TURNSTILE_FAILED"
  /** 400: hace falta el nivel con clic. No es un error para la persona: el form lo resuelve solo. */
  | "TURNSTILE_STEP_UP"
  /** 429: se llegó a un límite de envíos. */
  | "OTP_LIMIT"
  /** 403: el pedido no pasó las señales duras (navegador automatizado, campo trampa). */
  | "OTP_SEND_BLOCKED";

export interface AuthGuardErrorInfo {
  code: AuthGuardErrorCode;
  title: string;
  body: string;
  /** How long to hold the submit button before letting them try again. 0 = no wait. */
  retryAfterSeconds: number;
  /** Sólo en el step-up: la sitekey del nivel con clic (por si la config no la traía). */
  managedSiteKey: string | null;
  /**
   * Sólo en OTP_LIMIT: qué límite fue (`limit_phone`, `limit_ip`, `limit_country`…). Dice si
   * cambiar de número destraba (ver `isPerNumberLimit`).
   */
  limitReason: string | null;
}

/**
 * Límites que dependen del NÚMERO: con otro número (u otro país) la espera ya no corre. Los de IP,
 * dispositivo y el presupuesto del día siguen aunque se cambie el número.
 */
const PER_NUMBER_LIMITS = new Set(["limit_phone", "limit_country"]);

export function isPerNumberLimit(info: Pick<AuthGuardErrorInfo, "code" | "limitReason">): boolean {
  return info.code === "OTP_LIMIT" && PER_NUMBER_LIMITS.has(info.limitReason ?? "");
}

/**
 * Longer, warmer copy than the server's one-liner. The cases read differently on purpose: only
 * some hint at anything the person might need to act on, and none of them accuse the person of
 * being a robot — plenty of real users hit this.
 */
const COPY: Record<
  Exclude<AuthGuardErrorCode, "OTP_LIMIT">,
  { title: string; body: string; retryAfterSeconds: number }
> = {
  TURNSTILE_PENDING: {
    title: "Estamos comprobando que seas una persona",
    body: "Es automático y tarda un segundo. No perdiste nada de lo que escribiste.",
    retryAfterSeconds: FALLBACK_RETRY_SECONDS,
  },
  TURNSTILE_EXPIRED: {
    title: "La comprobación se venció",
    body: "Pasó un rato desde que abriste el formulario. Ya pedimos una nueva.",
    retryAfterSeconds: FALLBACK_RETRY_SECONDS,
  },
  TURNSTILE_FAILED: {
    title: "No pudimos terminar la comprobación",
    body: "Suele ser la conexión, o una extensión del navegador que la bloquea.",
    retryAfterSeconds: FALLBACK_RETRY_SECONDS,
  },
  TURNSTILE_STEP_UP: {
    title: "Confirmá que sos una persona",
    body: "Es un toque, para frenar a los robots.",
    retryAfterSeconds: 0,
  },
  OTP_SEND_BLOCKED: {
    title: "No pudimos verificar este pedido",
    body: "Probá de nuevo desde otro navegador.",
    retryAfterSeconds: 0,
  },
};

const CODES = new Set<string>([...Object.keys(COPY), "OTP_LIMIT"]);

function isAuthGuardCode(value: unknown): value is AuthGuardErrorCode {
  return typeof value === "string" && CODES.has(value);
}

export function getAuthGuardError(error: unknown): AuthGuardErrorInfo | null {
  if (!(error instanceof AxiosError)) return null;

  const data = error.response?.data as
    | {
        code?: unknown;
        message?: unknown;
        retryAfterSeconds?: unknown;
        managedSiteKey?: unknown;
        reason?: unknown;
      }
    | undefined;
  const code = data?.code;
  if (!isAuthGuardCode(code)) return null;

  const retry = Number(data?.retryAfterSeconds);
  const managedSiteKey =
    typeof data?.managedSiteKey === "string" && data.managedSiteKey.trim()
      ? data.managedSiteKey.trim()
      : null;

  if (code === "OTP_LIMIT") {
    // El mensaje de la API dice cuál límite y cuánto esperar ("Esperá unos 12 minutos…"): es
    // más preciso que cualquier texto genérico de acá.
    const message = typeof data?.message === "string" && data.message.trim();
    return {
      code,
      title: "Por ahora no podemos mandarte otro código",
      body: message || "Esperá un rato y probá de nuevo.",
      retryAfterSeconds:
        Number.isFinite(retry) && retry > 0 ? Math.ceil(retry) : FALLBACK_LIMIT_SECONDS,
      managedSiteKey: null,
      limitReason: typeof data?.reason === "string" && data.reason ? data.reason : null,
    };
  }

  const copy = COPY[code];
  return {
    code,
    title: copy.title,
    body: copy.body,
    retryAfterSeconds:
      copy.retryAfterSeconds > 0 && Number.isFinite(retry) && retry > 0
        ? Math.ceil(retry)
        : copy.retryAfterSeconds,
    managedSiteKey,
    limitReason: null,
  };
}

/** "45s", "12 min", "2 h": la espera del botón, legible aunque sea larga (límites de envío). */
export function formatRetryWait(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.ceil(s / 60)} min`;
  return `${Math.ceil(s / 3600)} h`;
}
